import React, {useEffect, useMemo, useState} from "react";
import {NavLink, useParams} from "react-router-dom";
import {Tab, TabList, TabPanel, Tabs} from "react-tabs";
import {useTranslation} from "react-i18next";

import ConnectionSettings from "./components/ConnectionSettings";
import ServerSettings from "./components/ServerSettings";
import ControllersSettings from "./components/ControllersSettings";

import RelaysSettings from "./components/RelaysSettings";

import "./SettingsPage.css";
import {
  Diagnostics,
  DigitalDriverSettingsModel,
  droneService,
  DroneSettingsModel,
  Header,
  NotificationType,
  RelaySettingsModel,
  setNotificationMessage,
  setNotificationStatus,
  settingsService,
  useAppDispatch,
  useAppSelector,
  UserRole
} from "@shared/ui";
import CameraSettings from "./components/CameraSettings/CameraVideoSettings";

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { droneId } = useParams();
  const dispatch = useAppDispatch();
  const [settings, setSettings] = useState<DroneSettingsModel>({
    serverAddress: "control.roboticsystems.com.ua",
    serverPort: 443,
    serverPath: "/api",
    cameras: undefined,
    keepAliveIntervalMs: 500,
    serverTimeoutMs: 1500,
    bmsPort: "/dev/ttyAMA4",
    gpsPort: "/dev/ttyAMA0",
    gpsBaudRate: 115200,
    manageConnectionGateways: false,
    digitalDriverSettings: {
      rightBackwardUs: 1000,
      rightStopUs: 1500,
      rightForwardUs: 2000,
      leftBackwardUs: 1000,
      leftStopUs: 1500,
      leftForwardUs: 2000,
    },
    relaySettings: undefined,
  });
  const [isNew, setIsNew] = useState<boolean>(false);
  const [isVPNConnected, setIsVPNConnected] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [softwareVersion, setSoftwareVersion] = useState<string>();
  const user = useAppSelector((store) => store.user.value);
  const versions = useAppSelector((store) => store.versions.value);

  const caps = useMemo(
    () => versions.find(v => v.name == softwareVersion)?.capabilities.map(c => c.name) ?? [],
    [versions, softwareVersion],
  );

  useEffect(() => {
    if (droneId) {
      droneService.getDrone(droneId)
          .then(data => {
            setSoftwareVersion(data.softwareVersion);
          });
      if (user!.role == UserRole.SuperAdmin || user!.role == UserRole.ServiceAdmin) {
        settingsService.getSettings(droneId).then((data) => {
          if (!data.settings) return;

          setSettings((prev) => ({
            serverAddress: data.settings?.serverAddress ?? prev.serverAddress,
            serverPort: data.settings?.serverPort ?? prev.serverPort,
            serverPath: data.settings?.serverPath ?? prev.serverPath,
            cameras: data.settings?.cameras ?? prev.cameras,
            keepAliveIntervalMs:
                data.settings?.keepAliveIntervalMs ?? prev.keepAliveIntervalMs,
            serverTimeoutMs:
                data.settings?.serverTimeoutMs ?? prev.serverTimeoutMs,
            bmsPort: data.settings?.bmsPort ?? prev.bmsPort,
            gpsPort: data.settings?.gpsPort ?? prev.gpsPort,
            gpsBaudRate: data.settings?.gpsBaudRate ?? prev.gpsBaudRate,
            manageConnectionGateways:
                data.settings?.manageConnectionGateways ??
                prev.manageConnectionGateways,
            digitalDriverSettings: data.settings?.digitalDriverSettings
                ?.rightStopUs
                ? data.settings?.digitalDriverSettings
                : prev.digitalDriverSettings,
            relaySettings: data.settings?.relaySettings ?? prev.relaySettings,
          }));

          setIsNew(data.isNew);
          setIsVPNConnected(data.isVPNConnected);
        });
      }
    }
  }, [droneId]);

  const save = async () => {
    if (droneId && settings) {
      await settingsService.saveSettings(droneId, settings);
      dispatch(
        setNotificationMessage({
          message: t("settingsPage.notifications.settingsSaved"),
          type: NotificationType.Message,
        }),
      );
      dispatch(setNotificationStatus(true));
    }
  };

  const saveAddress = (value: string) => {
    if (settings) {
      setSettings({ ...settings, serverAddress: value });
    }
  };

  const savePort = (value: string) => {
    if (settings) {
      setSettings({ ...settings, serverPort: +value });
    }
  };

  const savePath = (value: string) => {
    if (settings) {
      setSettings({ ...settings, serverPath: value });
    }
  };

  const saveKeepAlive = (value: string) => {
    if (settings) {
      setSettings({ ...settings, keepAliveIntervalMs: +value });
    }
  };

  const saveTimeout = (value: string) => {
    if (settings) {
      setSettings({ ...settings, serverTimeoutMs: +value });
    }
  };

  const saveBMSPort = (value: string) => {
    if (settings) {
      setSettings({ ...settings, bmsPort: value });
    }
  };

  const saveGPSPort = (value: string) => {
    if (settings) {
      setSettings({ ...settings, gpsPort: value });
    }
  };

  const saveGPSRate = (value: string) => {
    if (settings) {
      setSettings({ ...settings, gpsBaudRate: +value });
    }
  };

  const saveManageGateways = (value: boolean) => {
    if (settings) {
      setSettings({ ...settings, manageConnectionGateways: value });
    }
  };

  const saveDriverSettings = (
    key: keyof DigitalDriverSettingsModel,
    value: string,
  ) => {
    if (settings && settings.digitalDriverSettings) {
      let drivers = { ...settings.digitalDriverSettings };
      drivers[key] = +value;
      setSettings({ ...settings, digitalDriverSettings: drivers });
    }
  };

  const saveRelaysSettings = (
    index: number,
    key: keyof RelaySettingsModel,
    value: string,
  ) => {
    if (!settings?.relaySettings) return;
    console.log(value);

    const newRelays = [...settings.relaySettings];
    newRelays[index] = { ...newRelays[index], [key]: Number(value) };
    setSettings({ ...settings, relaySettings: newRelays });
  };

  const addRelay = () => {
    if (!settings) return;

    const newRelay: RelaySettingsModel = {
      channel: undefined,
      pin: undefined,
      value: undefined,
    };

    const newRelays = settings.relaySettings
      ? [...settings.relaySettings, newRelay]
      : [newRelay];
    setSettings({ ...settings, relaySettings: newRelays });
  };

  const deleteRelay = () => {
    if (!settings?.relaySettings || settings.relaySettings.length === 0) return;

    const newRelays = settings.relaySettings.slice(0, -1);
    setSettings({ ...settings, relaySettings: newRelays });
  };

  return (
    <div className="container">
      <Header />
      <NavLink className="btn btn-text text-left" to={"/dashboard/"}>
        {t("settingsPage.backButton")}
      </NavLink>
      <div className="mt-20">
        <Tabs
          selectedIndex={activeIndex}
          onSelect={(index) => setActiveIndex(index)}
          className="settings-wrapper"
        >
          <TabList className="settings-tablist-wrapper">
            {(user!.role == UserRole.SuperAdmin || user!.role == UserRole.ServiceAdmin) && <Tab>{t("settingsPage.tabs.maintenance")}</Tab>}
            {(user!.role == UserRole.SuperAdmin || user!.role == UserRole.ServiceAdmin) && <Tab>{t("settingsPage.tabs.generalSettings")}</Tab>}
            {(user!.role == UserRole.SuperAdmin || user!.role == UserRole.ServiceAdmin) && <Tab>{t("settingsPage.tabs.controllersSettings")}</Tab>}
            {(user!.role == UserRole.SuperAdmin || user!.role == UserRole.ServiceAdmin) && <Tab>{t("settingsPage.tabs.relaySettings")}</Tab>}
            {(user!.role == UserRole.SuperAdmin || user!.role == UserRole.ServiceAdmin) && <Tab>{t("settingsPage.tabs.monitor")}</Tab>}
            <Tab>{t("settingsPage.tabs.cameraSettings")}</Tab>
          </TabList>
          {(user!.role == UserRole.SuperAdmin || user!.role == UserRole.ServiceAdmin) && <TabPanel>
            <ConnectionSettings
              droneId={droneId}
              isNew={isNew}
              isVPNConnected={isVPNConnected}
              setIsNew={setIsNew}
            />
          </TabPanel>}

          {(user!.role == UserRole.SuperAdmin || user!.role == UserRole.ServiceAdmin) && <TabPanel>
            <ServerSettings
              settings={settings}
              saveAddress={saveAddress}
              savePort={savePort}
              savePath={savePath}
              saveKeepAlive={saveKeepAlive}
              saveTimeout={saveTimeout}
              saveBMSPort={saveBMSPort}
              saveGPSPort={saveGPSPort}
              saveGPSRate={saveGPSRate}
              saveManageGateways={saveManageGateways}
            />
          </TabPanel>}

          {(user!.role == UserRole.SuperAdmin || user!.role == UserRole.ServiceAdmin) && <TabPanel>
            <ControllersSettings
              settings={settings.digitalDriverSettings}
              saveDriverSettings={saveDriverSettings}
            />
          </TabPanel>}

          {(user!.role == UserRole.SuperAdmin || user!.role == UserRole.ServiceAdmin) && <TabPanel>
            {settings.relaySettings?.map((item, index) => (
              <RelaysSettings
                key={index}
                settings={item}
                saveRelaysSettings={(key, value) =>
                  saveRelaysSettings(index, key, value)
                }
              />
            ))}
            <div className="p-20">
              <button className="btn" onClick={addRelay}>
                {t("settingsPage.buttons.addRelay")}
              </button>
              <button className="btn" onClick={deleteRelay}>
                {t("settingsPage.buttons.deleteRelay")}
              </button>
            </div>
          </TabPanel>}

          {(user!.role == UserRole.SuperAdmin || user!.role == UserRole.ServiceAdmin) && <TabPanel>{droneId && <Diagnostics droneId={droneId} />}</TabPanel>}

          {caps.includes("VideoSettings") && <TabPanel>{droneId && <CameraSettings droneId={droneId} />}</TabPanel>}
        </Tabs>

        {(user!.role == UserRole.SuperAdmin || user!.role == UserRole.ServiceAdmin) &&
          activeIndex !== 4 && activeIndex !== 5 && activeIndex !== 6 && (
          <button className="btn" onClick={save}>
            {t("settingsPage.buttons.saveSettings")}
          </button>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
